import { useState, useMemo, useEffect } from 'react';
import { Settings2, AlertTriangle, Trash2, Copy, PanelRightClose, PanelRightOpen, LogOut, Check, Plus, GitBranch } from 'lucide-react';
import { Field, Input, Textarea, Select, Toggle, Slider } from '../../components/ui/Field';
import { Button } from '../../components/ui/Button';
import { OVERSIGHT_MODES, NODE_DEF_BY_KIND, TOOL_OPTIONS } from '../../lib/catalog';
import { toRules, newRule, rulesAreNormalized } from '../../lib/router';
import { useFlowStore } from '../../store/flowStore';
import { useModelsStore, groupModelsByProvider } from '../../store/modelsStore';
import { cn } from '../../lib/utils';
import type { Agent, KnowledgeBase, Person, HumanOversightMode } from '../../types';

export function PropertiesPanel({ agents, kbs, people }: { agents: Agent[]; kbs: KnowledgeBase[]; people: Person[] }) {
  const [collapsed, setCollapsed] = useState(false);
  const catalog = useModelsStore((s) => s.catalog);
  const modelGroups = useMemo(() => groupModelsByProvider(catalog), [catalog]);
  const selectedId = useFlowStore((s) => s.selectedNodeId);
  const node = useFlowStore((s) => s.nodes.find((n) => n.id === s.selectedNodeId));
  const flowOversight = useFlowStore((s) => s.meta?.oversightMode);
  const update = useFlowStore((s) => s.updateNodeConfig);
  const setOversight = useFlowStore((s) => s.setNodeOversight);
  const requestDeleteNodes = useFlowStore((s) => s.requestDeleteNodes);
  const duplicateNode = useFlowStore((s) => s.duplicateNode);
  const reparentNode = useFlowStore((s) => s.reparentNode);

  // Migra condições legadas (string[]) do roteador para regras com id estável,
  // para que cada saída (handle) tenha um id consistente ao ligar arestas.
  useEffect(() => {
    if (node?.data.kind === 'router' && !rulesAreNormalized(node.data.config.conditions)) {
      update(node.id, { conditions: toRules(node.data.config.conditions).map((r) => newRule(r.label)) });
    }
  }, [node, update]);

  if (collapsed) {
    return (
      <div className="flex w-12 shrink-0 flex-col items-center border-l border-line bg-white py-3">
        <button
          onClick={() => setCollapsed(false)}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-slate hover:bg-cloud hover:text-ink"
          title="Expandir propriedades"
          aria-label="Expandir propriedades"
        >
          <PanelRightOpen className="h-4 w-4" />
        </button>
        <div className="mt-2 flex h-9 w-9 items-center justify-center rounded-lg text-slate">
          <Settings2 className="h-4 w-4" />
        </div>
      </div>
    );
  }

  if (!selectedId || !node) {
    return (
      <div className="flex w-80 shrink-0 flex-col border-l border-line bg-white">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">Propriedades</h2>
          <button
            onClick={() => setCollapsed(true)}
            className="rounded-lg p-1.5 text-slate hover:bg-cloud hover:text-ink"
            title="Colapsar propriedades"
            aria-label="Colapsar propriedades"
          >
            <PanelRightClose className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <Settings2 className="h-8 w-8 text-slate/50" />
          <p className="mt-3 text-sm text-slate">Selecione um nó para editar suas propriedades.</p>
        </div>
      </div>
    );
  }

  const def = NODE_DEF_BY_KIND[node.data.kind];
  const c = node.data.config;
  const set = (patch: Record<string, unknown>) => update(node.id, patch);

  const effectiveMode = (node.data.oversightOverride ?? flowOversight) as HumanOversightMode | undefined;
  const linkedAgent = node.data.kind === 'agent' ? agents.find((a) => a.id === c.agentId) : undefined;
  const govConflict =
    effectiveMode === 'out_of_the_loop' && !!linkedAgent?.guardrails.humanApprovalActions.length;

  return (
    <div className="flex w-80 shrink-0 flex-col border-l border-line bg-white">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg text-white" style={{ backgroundColor: def.color }}>
            <def.icon className="h-4 w-4" />
          </div>
          <h2 className="text-sm font-semibold text-ink">{def.label}</h2>
        </div>
        <div className="flex gap-1">
          <button onClick={() => duplicateNode(node.id)} className="rounded-lg p-1.5 text-slate hover:bg-cloud" aria-label="Duplicar">
            <Copy className="h-4 w-4" />
          </button>
          <button onClick={() => requestDeleteNodes([node.id])} className="rounded-lg p-1.5 text-slate hover:bg-danger/10 hover:text-danger" aria-label="Excluir">
            <Trash2 className="h-4 w-4" />
          </button>
          <button onClick={() => setCollapsed(true)} className="rounded-lg p-1.5 text-slate hover:bg-cloud hover:text-ink" aria-label="Colapsar propriedades" title="Colapsar propriedades">
            <PanelRightClose className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
        <Field label="Rótulo" hint="opcional" className="mb-5">
          <Input value={(c.label as string) ?? ''} placeholder={def.label} onChange={(e) => set({ label: e.target.value })} />
        </Field>

        {node.parentId && (
          <div className="mb-5 flex items-center justify-between rounded-xl border border-node-loop/30 bg-node-loop/5 px-3 py-2">
            <span className="text-xs font-medium text-node-loop">Dentro de um loop</span>
            <button
              onClick={() => reparentNode(node.id, null)}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-slate hover:bg-white hover:text-ink"
            >
              <LogOut className="h-3.5 w-3.5" /> Tirar do loop
            </button>
          </div>
        )}

        {/* Config por tipo */}
        {node.data.kind === 'trigger' && (
          <>
            <Field label="Tipo de gatilho">
              <Select value={(c.triggerType as string) ?? 'manual'} onChange={(e) => set({ triggerType: e.target.value })}>
                <option value="manual">Manual</option>
                <option value="webhook">Webhook (mock)</option>
                <option value="scheduled">Agendado</option>
              </Select>
            </Field>
            {c.triggerType === 'webhook' && (
              <Field label="URL do webhook" hint="mock — gerada pelo backend">
                <Input readOnly value={`https://api.compraagora.app/hooks/${node.id}`} className="font-mono text-xs text-slate" />
              </Field>
            )}
            {c.triggerType === 'scheduled' && (
              <Field label="Frequência">
                <Select value={(c.schedule as string) ?? 'daily'} onChange={(e) => set({ schedule: e.target.value })}>
                  <option value="hourly">A cada hora</option>
                  <option value="daily">Diariamente</option>
                  <option value="weekly">Semanalmente</option>
                  <option value="cron">Cron personalizado</option>
                </Select>
              </Field>
            )}
            {c.triggerType === 'scheduled' && c.schedule === 'cron' && (
              <Field label="Expressão cron">
                <Input className="font-mono text-xs" placeholder="0 9 * * 1-5" value={(c.cron as string) ?? ''} onChange={(e) => set({ cron: e.target.value })} />
              </Field>
            )}
            <Field label="Payload de exemplo" className="mb-0">
              <Textarea rows={5} className="font-mono text-xs" value={(c.samplePayload as string) ?? ''} onChange={(e) => set({ samplePayload: e.target.value })} />
            </Field>
          </>
        )}

        {node.data.kind === 'agent' && (
          <>
            <Field label="Origem do agente">
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => set({ source: 'existing' })}
                  className={cn(
                    'flex-1 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                    (c.source ?? 'existing') === 'existing' ? 'border-brand-orange bg-brand-orange/5 text-ink' : 'border-line text-slate hover:bg-cloud',
                  )}
                >
                  Agente existente
                </button>
                <button
                  type="button"
                  onClick={() => set({ source: 'inline' })}
                  className={cn(
                    'flex-1 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                    c.source === 'inline' ? 'border-brand-orange bg-brand-orange/5 text-ink' : 'border-line text-slate hover:bg-cloud',
                  )}
                >
                  Criar aqui
                </button>
              </div>
            </Field>

            {(c.source ?? 'existing') === 'existing' ? (
              <Field label="Agente" className="mb-0">
                <Select value={(c.agentId as string) ?? ''} onChange={(e) => set({ agentId: e.target.value || undefined })}>
                  <option value="">Selecione…</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </Select>
                {linkedAgent && (
                  <p className="mt-2 text-xs text-slate">
                    Herda persona, {linkedAgent.tools.length} ferramentas e arquivos (RAG) do agente.
                  </p>
                )}
              </Field>
            ) : (
              <>
                <Field label="Nome (rótulo do agente)">
                  <Input value={(c.inlineName as string) ?? ''} placeholder="ex.: Triador" onChange={(e) => set({ inlineName: e.target.value })} />
                </Field>
                <Field label="Modelo">
                  <Select value={(c.inlineModel as string) ?? ''} onChange={(e) => set({ inlineModel: e.target.value })}>
                    <option value="">Selecione…</option>
                    {modelGroups.map((g) => (
                      <optgroup key={g.provider} label={g.provider}>
                        {g.models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                      </optgroup>
                    ))}
                  </Select>
                </Field>
                <Field label="Persona / instruções">
                  <Textarea rows={5} value={(c.inlinePersona as string) ?? ''} placeholder="Você é um agente que…" onChange={(e) => set({ inlinePersona: e.target.value })} />
                </Field>
                <div className="mb-0">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-sm font-medium text-ink">Temperatura</span>
                    <span className="rounded-md bg-cloud px-2 py-0.5 text-xs font-semibold text-ink">
                      {((c.inlineTemperature as number) ?? 0.4).toFixed(1)}
                    </span>
                  </div>
                  <Slider value={(c.inlineTemperature as number) ?? 0.4} onChange={(v) => set({ inlineTemperature: v })} />
                </div>
                <p className="mt-2 rounded-lg bg-cloud px-3 py-2 text-xs text-slate">
                  Definido só neste fluxo — não cria um agente em /agents.
                </p>
              </>
            )}
          </>
        )}

        {node.data.kind === 'llm' && (
          <>
            <Field label="Modelo">
              <Select value={(c.model as string) ?? ''} onChange={(e) => set({ model: e.target.value })}>
                {modelGroups.map((g) => (
                  <optgroup key={g.provider} label={g.provider}>
                    {g.models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </optgroup>
                ))}
              </Select>
            </Field>
            <Field label="Prompt">
              <Textarea rows={4} value={(c.prompt as string) ?? ''} onChange={(e) => set({ prompt: e.target.value })} />
            </Field>
            <Field label="Variáveis de entrada" hint="separadas por vírgula">
              <Input value={(c.variables as string) ?? ''} onChange={(e) => set({ variables: e.target.value })} />
            </Field>
            <div className="mb-4">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-medium text-ink">Temperatura</span>
                <span className="rounded-md bg-cloud px-2 py-0.5 text-xs font-semibold text-ink">
                  {((c.temperature as number) ?? 0.4).toFixed(1)}
                </span>
              </div>
              <Slider value={(c.temperature as number) ?? 0.4} onChange={(v) => set({ temperature: v })} />
            </div>
            <Field label="Máx. tokens" className="mb-0">
              <Input type="number" value={(c.maxTokens as number) ?? 2048} onChange={(e) => set({ maxTokens: Number(e.target.value) })} />
            </Field>
          </>
        )}

        {node.data.kind === 'tool' && (
          <>
            <Field label="Ferramenta">
              <Select value={(c.tool as string) ?? 'rag'} onChange={(e) => set({ tool: e.target.value })}>
                {TOOL_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </Field>
            <Field label="Parâmetros (JSON)" className="mb-0">
              <Textarea rows={4} className="font-mono text-xs" value={(c.params as string) ?? ''} onChange={(e) => set({ params: e.target.value })} />
            </Field>
          </>
        )}

        {node.data.kind === 'router' && (
          <RouterRulesEditor
            conditions={c.conditions}
            onChange={(rules) => set({ conditions: rules })}
          />
        )}

        {node.data.kind === 'loop' && (
          <>
            <Field label="Modo">
              <Select value={(c.mode as string) ?? 'max_iterations'} onChange={(e) => set({ mode: e.target.value })}>
                <option value="for_each">For-each</option>
                <option value="while">While</option>
                <option value="until">Until-condition</option>
                <option value="max_iterations">Max-iterations</option>
              </Select>
            </Field>
            {c.mode === 'for_each' && (
              <Field label="Coleção / variável a iterar" hint="for-each">
                <Input value={(c.collection as string) ?? ''} placeholder="ex.: claims, itens" onChange={(e) => set({ collection: e.target.value })} />
              </Field>
            )}
            {(c.mode === 'while' || c.mode === 'until') && (
              <Field label={c.mode === 'while' ? 'Repetir enquanto' : 'Repetir até'}>
                <Input value={(c.stopCondition as string) ?? ''} placeholder="ex.: campos faltantes == 0" onChange={(e) => set({ stopCondition: e.target.value })} />
              </Field>
            )}
            <div className="mb-4">
              <Toggle checked={!!c.parallel} onChange={(v) => set({ parallel: v })} label="Execução paralela" description="Itera o corpo em paralelo (Loop Paralelo)." />
            </div>
            <Field label="Máx. de iterações" hint="obrigatório — salvaguarda contra loop infinito" className="mb-0">
              <Input type="number" min={1} value={(c.maxIterations as number) ?? 5} onChange={(e) => set({ maxIterations: Math.max(1, Number(e.target.value)) })} />
            </Field>
            <p className="mt-3 rounded-lg bg-cloud px-3 py-2 text-xs text-slate">
              Arraste nós para dentro do container para montar o corpo do loop.
            </p>
          </>
        )}

        {node.data.kind === 'memory' && (
          <>
            <Field label="Operação">
              <Select value={(c.operation as string) ?? 'read'} onChange={(e) => set({ operation: e.target.value })}>
                <option value="read">Ler</option>
                <option value="write">Escrever</option>
              </Select>
            </Field>
            <Field label="Escopo">
              <Select value={(c.scope as string) ?? 'short'} onChange={(e) => set({ scope: e.target.value })}>
                <option value="short">Curto prazo</option>
                <option value="long">Longo prazo</option>
              </Select>
            </Field>
            <Field label="Chave" className={c.operation === 'write' ? 'mb-4' : 'mb-0'}>
              <Input value={(c.key as string) ?? ''} placeholder="ex.: claim_atual" onChange={(e) => set({ key: e.target.value })} />
            </Field>
            {c.operation === 'write' && (
              <Field label="Valor a gravar" hint="texto ou variável" className="mb-0">
                <Textarea rows={3} value={(c.value as string) ?? ''} onChange={(e) => set({ value: e.target.value })} />
              </Field>
            )}
          </>
        )}

        {node.data.kind === 'kb' && (
          <>
            <Field label="Base de conhecimento">
              <Select value={(c.knowledgeBaseId as string) ?? ''} onChange={(e) => set({ knowledgeBaseId: e.target.value || undefined })}>
                <option value="">Selecione…</option>
                {kbs.map((kb) => <option key={kb.id} value={kb.id}>{kb.name}</option>)}
              </Select>
            </Field>
            <Field label="Top-k" hint="nº de trechos recuperados">
              <Input type="number" min={1} max={50} value={(c.topK as number) ?? 5} onChange={(e) => set({ topK: Number(e.target.value) })} />
            </Field>
            <div className="mb-0">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-medium text-ink">Threshold de similaridade</span>
                <span className="rounded-md bg-cloud px-2 py-0.5 text-xs font-semibold text-ink">
                  {((c.threshold as number) ?? 0.7).toFixed(2)}
                </span>
              </div>
              <Slider value={(c.threshold as number) ?? 0.7} min={0} max={1} step={0.05} onChange={(v) => set({ threshold: v })} />
            </div>
          </>
        )}

        {node.data.kind === 'human_review' && (
          <>
            <Field label="Mensagem ao revisor">
              <Textarea rows={3} value={(c.message as string) ?? ''} onChange={(e) => set({ message: e.target.value })} />
            </Field>
            <Field label="Responsáveis pela aprovação" hint="quem pode aprovar">
              <ApproverPicker
                people={people}
                value={(c.approvers as string[]) ?? []}
                onChange={(ids) => set({ approvers: ids })}
              />
            </Field>
            <Field label="SLA (minutos)">
              <Input type="number" value={(c.slaMinutes as number) ?? 30} onChange={(e) => set({ slaMinutes: Number(e.target.value) })} />
            </Field>
            <Field label="Fallback sem resposta" className="mb-0">
              <Select value={(c.fallback as string) ?? 'approve'} onChange={(e) => set({ fallback: e.target.value })}>
                <option value="approve">Aprovar automaticamente</option>
                <option value="reject">Rejeitar automaticamente</option>
              </Select>
            </Field>
          </>
        )}

        {node.data.kind === 'output' && (
          <>
            <Field label="Formato">
              <Select value={(c.format as string) ?? 'text'} onChange={(e) => set({ format: e.target.value })}>
                <option value="text">Texto</option>
                <option value="json">JSON</option>
              </Select>
            </Field>
            <Field label="Destino (mock)" className="mb-0">
              <Select value={(c.destination as string) ?? 'chat'} onChange={(e) => set({ destination: e.target.value })}>
                <option value="chat">Chat</option>
                <option value="email">E-mail</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="crm">CRM</option>
                <option value="erp">ERP</option>
                <option value="sharepoint">SharePoint</option>
                <option value="webhook">Webhook</option>
                <option value="console">Console</option>
              </Select>
            </Field>
          </>
        )}

        {/* Sobrescrita de supervisão por nó */}
        <div className="mt-6 border-t border-line pt-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate">Supervisão deste nó</h3>
          <div className="space-y-1.5">
            <OverrideOption label="Herdar do fluxo" active={!node.data.oversightOverride} onClick={() => setOversight(node.id, undefined)} />
            {OVERSIGHT_MODES.map((m) => (
              <OverrideOption
                key={m.mode}
                label={m.label}
                active={node.data.oversightOverride === m.mode}
                color={m.color}
                onClick={() => setOversight(node.id, m.mode)}
              />
            ))}
          </div>

          {govConflict && (
            <div className="mt-3 flex gap-2 rounded-xl border border-warning/30 bg-warning/10 p-3 text-xs text-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                Conflito de governança: o agente exige aprovação humana, mas este nó está fora do loop (ação sensível sem ponto de revisão).
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Editor de regras de roteamento. Cada regra é uma SAÍDA do nó (handle no canvas):
// ligue a aresta no ponto da regra para definir o destino daquele caminho.
function RouterRulesEditor({ conditions, onChange }: { conditions: unknown; onChange: (rules: { id: string; label: string }[]) => void }) {
  const rules = toRules(conditions);
  const setLabel = (id: string, label: string) => onChange(rules.map((r) => (r.id === id ? { ...r, label } : r)));
  const add = () => onChange([...rules, newRule('')]);
  const remove = (id: string) => onChange(rules.filter((r) => r.id !== id));

  return (
    <Field
      label="Regras de roteamento"
      hint="o modelo escolhe UMA regra pela entrada; ligue cada regra a um destino no canvas"
      className="mb-0"
    >
      <div className="space-y-2">
        {rules.length === 0 && (
          <p className="rounded-lg bg-cloud px-3 py-2 text-xs text-slate">Nenhuma regra ainda. Adicione a primeira abaixo.</p>
        )}
        {rules.map((r, i) => (
          <div key={r.id} className="flex items-center gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-cloud text-[11px] font-semibold text-slate">
              {i + 1}
            </span>
            <Input
              value={r.label}
              placeholder="ex.: mensagem é uma reclamação"
              onChange={(e) => setLabel(r.id, e.target.value)}
            />
            <button
              onClick={() => remove(r.id)}
              className="shrink-0 rounded-lg p-1.5 text-slate hover:bg-danger/10 hover:text-danger"
              aria-label="Remover regra"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <Button variant="secondary" size="sm" icon={Plus} onClick={add} className="w-full">
          Adicionar regra
        </Button>
        <p className="flex items-start gap-1.5 rounded-lg bg-cloud px-3 py-2 text-[11px] text-slate">
          <GitBranch className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Cada regra vira um ponto de saída no nó. Arraste do ponto da regra até o próximo nó para definir aquele caminho.
        </p>
      </div>
    </Field>
  );
}

// Seletor de múltiplos aprovadores (pessoas) para o nó Human Review.
function ApproverPicker({ people, value, onChange }: { people: Person[]; value: string[]; onChange: (ids: string[]) => void }) {
  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }
  if (!people.length) {
    return <p className="rounded-lg bg-cloud px-3 py-2 text-xs text-slate">Cadastre pessoas em “Áreas & Budget”.</p>;
  }
  return (
    <div className="max-h-44 space-y-1 overflow-y-auto scrollbar-thin rounded-xl border border-line p-1.5">
      {people.map((p) => {
        const active = value.includes(p.id);
        return (
          <button
            key={p.id}
            onClick={() => toggle(p.id)}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors',
              active ? 'bg-brand-orange/5' : 'hover:bg-cloud',
            )}
          >
            <span className={cn('flex h-4 w-4 items-center justify-center rounded border', active ? 'border-brand-orange bg-brand-orange text-white' : 'border-line')}>
              {active && <Check className="h-3 w-3" />}
            </span>
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-navy text-[10px] font-semibold text-white">
              {p.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-ink">{p.name}</span>
              <span className="block truncate text-[11px] text-slate">{p.role || p.email}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function OverrideOption({ label, active, color, onClick }: { label: string; active: boolean; color?: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
        active ? 'border-brand-orange bg-brand-orange/5 text-ink' : 'border-line text-slate hover:bg-cloud',
      )}
    >
      <span>{label}</span>
      {active && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color ?? '#E11019' }} />}
    </button>
  );
}
